// Morning-action badge colour + label maps, keyed by ActionCategory. Pure (no React) so a
// coverage test can import them and assert they cover every server-emitted category. Typed
// `Record<ActionCategory, string>`, so TypeScript itself forces an entry for every union member
// (a missing key is a compile error, not a silent blank badge). Colour convention: red for an
// overdue/expired/critical item, amber for a due/expiring/pending one, ele-blue for a sales lead.
import type { ActionCategory } from "./api";

export const ACTION_BADGE: Record<ActionCategory, string> = {
  loler_overdue: "bg-red-600 text-white",
  ppm_visit_overdue: "bg-red-600 text-white",
  job_unassigned_soon: "bg-amber-400 text-black",
  callout_pending_approval: "bg-amber-400 text-black",
  loler_defect: "bg-red-600 text-white",
  // Session AKQ: same red-alert treatment as loler_defect -- an unresolved immediate lift defect
  // is the same urgency whichever of the two defect systems raised it.
  defect_no_client_notification: "bg-red-600 text-white",
  // Session BDB: a stopped SAFed stress test is an unresolved immediate lift defect, the same red alert.
  safed_test_stopped: "bg-red-600 text-white",
  complaint_overdue: "bg-red-600 text-white",
  sub_pl_below_minimum: "bg-red-600 text-white",
  cert_expired: "bg-red-600 text-white",
  sub_doc_expired: "bg-red-600 text-white",
  service_pending_approval: "bg-amber-400 text-black",
  contract_renewal_overdue: "bg-red-600 text-white",
  contract_renewal_due: "bg-amber-400 text-black",
  nps_below_target: "bg-amber-400 text-black",
  sub_pending_review: "bg-amber-400 text-black",
  cert_expiring: "bg-amber-400 text-black",
  env_objective_at_risk: "bg-amber-400 text-black",
  sub_doc_expiring: "bg-amber-400 text-black",
  quote_follow_up: "bg-ele-blue text-black",
  project_quote_follow_up: "bg-ele-blue text-black",
  // Session AHC: sales colour, matching the other follow-ups. An opportunity, not a problem.
  portfolio_discount_review: "bg-ele-blue text-black",
  prospect_cold: "bg-ele-blue text-black",
  // Session AVI: a lead whose lift is due, overdue or defective. Red: a safety matter as well as a sales opening.
  loler_precontract_lead: "bg-red-600 text-white",
  van_stock_reorder: "bg-amber-400 text-black",
  // Session ARY: Xero is (or may be) billing the wrong figure. Amber: put right in Xero, not an emergency.
  xero_billing_out_of_step: "bg-amber-400 text-black",
  // Session ARS (D91): a customer said yes and no repair job carries the work yet. Amber, a pending
  // decision rather than an overdue one.
  repair_estimate_needs_job: "bg-amber-400 text-black",
  // Session ATF: an engineer raised a repair estimate on site and it waits for the office's price. Amber, a pending
  // decision: the customer has seen nothing yet.
  repair_estimate_needs_price: "bg-amber-400 text-black",
  // Session PN1: engineers with notifications off. Amber, a pending fix: a job still reaches them by text if their text setting is on.
  engineer_notifications_off: "bg-amber-400 text-black",
  // Session BBF: an insurer's findings waiting for a check, or acted on. Amber: a pending decision, never an emergency.
  insurance_items_unbooked: "bg-amber-400 text-black",
  insurance_items_unscheduled: "bg-amber-400 text-black",
  insurance_item_needs_estimate: "bg-amber-400 text-black",
  insurance_item_verified: "bg-amber-400 text-black",
  // Session BBL: an approved job with no invoice, and a lift with repeat call-outs. Amber: put right by a person, not an emergency.
  job_uninvoiced: "bg-amber-400 text-black",
  repeat_callouts: "bg-amber-400 text-black",
  // Session SC6: a chase nobody has answered for over 30 days. Amber: a person decides, never an emergency.
  chase_no_response: "bg-amber-400 text-black",
  // Session SC6B: a planned visit that took twice as long as expected. Amber: a person looks, never an emergency.
  long_ppm_visit: "bg-amber-400 text-black",
  // Session PJ2: submitted site surveys waiting for the office. Amber: a person checks them, never an emergency.
  project_surveys_awaiting_review: "bg-amber-400 text-black",
  // Session BCD: an approved job's Xero invoice still a draft. Amber: a person authorises it in Xero, not an emergency.
  xero_draft_awaiting_authorisation: "bg-amber-400 text-black",
  // Session ARZ: a subcontractor's payment details need a person (verify, request, or send to Xero). Amber, a
  // pending check rather than an overdue one; a CHANGED set says so in its own title and ranks above the rest.
  subcontractor_payment_details: "bg-amber-400 text-black",
  // Session AUH: a chase that is now a phone call (amber, due), a debt ready to refer (red: the final notice has passed),
  // and a quote that ran its ladder with no answer (sales colour: a decision to close, not a problem).
  chase_call: "bg-amber-400 text-black",
  chase_referral: "bg-red-600 text-white",
  quote_close_no_response: "bg-ele-blue text-black",
};

export const ACTION_LABEL: Record<ActionCategory, string> = {
  loler_overdue: "LOLER overdue",
  ppm_visit_overdue: "PPM overdue",
  job_unassigned_soon: "Job unassigned",
  callout_pending_approval: "Call-out to release",
  loler_defect: "LOLER defect",
  defect_no_client_notification: "No client notice",
  safed_test_stopped: "SAFed test stopped",
  complaint_overdue: "Complaint overdue",
  sub_pl_below_minimum: "PL below minimum",
  cert_expired: "Cert expired",
  sub_doc_expired: "Sub doc expired",
  service_pending_approval: "Service to release",
  contract_renewal_overdue: "Renewal overdue",
  contract_renewal_due: "Renewal due",
  nps_below_target: "NPS below target",
  sub_pending_review: "Pending review",
  cert_expiring: "Cert expiring",
  env_objective_at_risk: "Objective at risk",
  sub_doc_expiring: "Sub doc expiring",
  quote_follow_up: "Quote follow-up",
  project_quote_follow_up: "Project quote follow-up",
  portfolio_discount_review: "Portfolio discount review",
  prospect_cold: "Cold lead",
  loler_precontract_lead: "Pre-contract LOLER lead",
  van_stock_reorder: "Van stock reorder",
  xero_billing_out_of_step: "Xero billing",
  repair_estimate_needs_job: "Approved estimate needs a decision",
  repair_estimate_needs_price: "Estimate from site needs a price",
  engineer_notifications_off: "Engineer notifications off",
  insurance_items_unbooked: "Insurer findings: book a check",
  insurance_items_unscheduled: "Insurer findings: no check scheduled",
  insurance_item_needs_estimate: "Insurer finding needs an estimate",
  insurance_item_verified: "Insurer finding confirmed present",
  job_uninvoiced: "Approved job, no invoice",
  repeat_callouts: "Repeat call-outs on one lift",
  chase_no_response: "Chase: no reply in 30 days",
  long_ppm_visit: "Planned visit took twice as long as expected",
  project_surveys_awaiting_review: "Site surveys waiting for review",
  xero_draft_awaiting_authorisation: "Xero draft to authorise",
  subcontractor_payment_details: "Payment details",
  chase_call: "Chase: phone them",
  chase_referral: "Debt ready to refer",
  quote_close_no_response: "Close quote: no response",
};
